import { message, Tooltip } from 'antd';
import { useRouter } from 'next/router';
import { memo, useCallback, useContext, useMemo } from 'react';

import { useAvatarProStore } from '@/3.0/ai-avatar-generator/store';
import { MemorizeImage } from '@/3.0/library/_components/operation/assets/_components/AssetCard';
import { getDownloadFeatureInfo } from '@/3.0/library/_components/operation/myCreations/_components/TaskCard';
import {
  ErrorIcon,
  ProIcon,
} from '@/3.0/library/_components/operation/myCreations/_components/TaskCard/icon';
import type { MenuItem } from '@/3.0/library/_components/operation/myCreations/_components/TaskCard/types';
import { useImageToVideoStore } from '@/3.0/video-generation/store/imageToVideoStore';
import {
  ImagePreviewCompareImperative,
  ImagePreviewImperative,
} from '@/components/common/ImagePreview/imperative';
import Loading from '@/components/common/Loading';
import MoreMenu from '@/components/common/MoreMenu';
import { VideoPlayerImperative } from '@/components/common/VideoPlayer/imperative';
import {
  type CombinedTaskItem,
  type MediaItem,
  MediaType,
  TaskState,
} from '@/components/DrawerTaskPanel/useTaskList';
import { RightContext } from '@/components/RightProvider/context';
import { PaymentModal, RenameAssetModal } from '@/components/UIComponents/CommonModal';
import { useRouteChange } from '@/hooks/useRouteChange';
import { downloadTaskFile } from '@/services';
import { deleteTask } from '@/services/common/batchTask';
import { deleteVideo } from '@/services/project';
import { F_CLOUD_TASK_TYPE } from '@/utils/const';
import {
  AssetClickEnum,
  DownloadTier,
  EventNewID,
  StatusEnum,
  SubscribeReasonEnum,
} from '@/utils/eventlog/eventNewConfig';
import { trackEvent } from '@/utils/eventlog/logAdapter';
import { downloadStreamFile } from '@/utils/fileUtil';
import { t } from '@/utils/helper/intl';
import { avatarVideoProURL, videoGenerateURL } from '@/utils/routeConstants';
import { PageTabParam } from '@/utils/router';

import styles from './ViralMyCreationTile.module.scss';

const MoreIcon = (
  <div className="rounded-[6px] bg-[#00000066] p-[2px]">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M9.39063 10C9.39063 9.66345 9.66345 9.39063 10 9.39063C10.3365 9.39063 10.6094 9.66345 10.6094 10C10.6094 10.3365 10.3365 10.6094 10 10.6094C9.66345 10.6094 9.39063 10.3365 9.39063 10Z"
        stroke="white"
        strokeWidth="1.21875"
      />
      <path
        d="M14.0625 10C14.0625 9.66345 14.3353 9.39063 14.6719 9.39063C15.0084 9.39063 15.2813 9.66345 15.2813 10C15.2813 10.3365 15.0084 10.6094 14.6719 10.6094C14.3353 10.6094 14.0625 10.3365 14.0625 10Z"
        stroke="white"
        strokeWidth="1.21875"
      />
      <path
        d="M4.71875 10C4.71875 9.66345 4.99158 9.39063 5.32812 9.39063C5.66467 9.39063 5.9375 9.66345 5.9375 10C5.9375 10.3366 5.66467 10.6094 5.32812 10.6094C4.99158 10.6094 4.71875 10.3366 4.71875 10Z"
        stroke="white"
        strokeWidth="1.21875"
      />
    </svg>
  </div>
);

function MaskOverlay({ state, errorMsg }: { state?: TaskState; errorMsg?: string }) {
  if (state === TaskState.PENDING) {
    return (
      <div className={styles.mask}>
        <div className={styles.maskSpinnerWrap}>
          <Loading size={20} />
        </div>
      </div>
    );
  }
  if (state === TaskState.FAILED) {
    return (
      <div className={styles.mask}>
        <div className={styles.maskError}>
          <ErrorIcon />
          <span className={styles.maskErrorText}>{errorMsg}</span>
        </div>
      </div>
    );
  }
  return null;
}

function PlayGlyph() {
  return (
    <div className={styles.playButton}>
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <circle cx="18" cy="18" r="18" fill="black" fillOpacity="0.24" />
        <path
          d="M25.6475 16.3143C26.8785 17.1008 26.8785 18.8987 25.6475 19.6851L15.9337 25.8911C14.6024 26.7417 12.8569 25.7855 12.857 24.2058L12.857 11.7937C12.857 10.2139 14.6024 9.25775 15.9337 10.1083L25.6475 16.3143Z"
          fill="white"
          fillOpacity="0.9"
        />
      </svg>
    </div>
  );
}

export interface ViralMyCreationTileProps {
  batch: CombinedTaskItem;
  media: MediaItem;
  onRename: (mediaItemId: number | string, newTitle: string) => void;
  onDeleteMedia: (mediaItemId: number | string) => void;
  onDeleteBatch: () => void;
}

/**
 * 爆款模板「我的创作」网格单格：独立 UI，行为对齐 library TaskCard / MediaPreview 的 VideoImagePreviewItem。
 */
const ViralMyCreationTile = memo(function ViralMyCreationTile({
  batch,
  media: item,
  onRename,
  onDeleteMedia,
  onDeleteBatch,
}: ViralMyCreationTileProps) {
  const router = useRouter();
  const { navigate } = useRouteChange();
  const rightContext = useContext(RightContext);
  const useIdentify = rightContext.getIdentity();
  const customType = Number(item.rawData?.customType) || 0;
  const type = item.mediaType;
  const state = item.state;

  const batchMediaCount = batch.mediaItems.length;
  const isLastItem = batchMediaCount === 1;

  const errorMsg = useMemo(() => {
    if (state === TaskState.NonCompliance) {
      return t('The-material-is-not-compliant');
    }
    if (state === TaskState.FAILED) {
      const rawData = item.rawData as any;
      const noTextErrorCode = [
        'TRANSLATE_INVALID_SRT_FILE',
        'OCR_NOT_FOUNT_SUBTITLES',
        'ERASE_NO_TEXT_DETECTED_IN_CROP_BOX',
      ];
      const noAudioErrorCode = [
        'ALGO_VOICE_DEMUCS_AUDIO_SILENT',
        'WHISPER_AUDIO_FILE_IS_SILENCE',
        'ALGO_VOICE_DEMUCS_NOT_AN_AUIDO_FILE',
      ];
      const langNotSupportErrorCode = ['1232901', 'OCR_INVALID_INPUT_REC_LANG'];

      if (noTextErrorCode.includes(rawData.engineCode || '')) {
        return t('No-text-content-detected');
      }
      if (noAudioErrorCode.includes(rawData.engineCode || '')) {
        return t('No-audio-content-detected');
      }
      if (langNotSupportErrorCode.includes(rawData.engineCode || '')) {
        return t('This-videos-original-language-is-not-supported');
      }
      return t('bc.task.fail.text');
    }
    return undefined;
  }, [state, item.rawData]);

  const downloadFile = useCallback(
    async ({
      download_tier,
      clickType,
    }: {
      download_tier: DownloadTier;
      clickType: AssetClickEnum;
    }) => {
      const businessId = (item.rawData as any)?.businessId || '';
      try {
        const { featureName, modelName: model_id } = getDownloadFeatureInfo(
          [item],
          batch.modelName,
        );

        trackEvent(EventNewID.ASSET_CLICK, {
          source: featureName,
          assetClickType: clickType,
        });
        trackEvent(EventNewID.CREATIONS_DOWNLOAD, {
          source: featureName,
          model_id,
          download_tier,
          task_id: businessId,
          template_result_name: item.title,
          template_name: item.rawData.extendInfo?.templateName,
        });
        const res = await downloadTaskFile([item.id]);
        if (res.code === 200) {
          const result = res.data || [];
          const array = result.map((down: any) => ({
            fileUrl: down.fileUrl,
            fileName: item.title || Date.now().toString(),
          }));
          downloadStreamFile(array);
          trackEvent(EventNewID.CREATIONS_DOWNLOAD_RESULT, {
            result: StatusEnum.SUCCESS,
            task_id: businessId,
          });
        } else {
          message.error(t('Download-Error'));
          trackEvent(EventNewID.CREATIONS_DOWNLOAD_RESULT, {
            result: StatusEnum.FAIL,
            task_id: businessId,
          });
        }
      } catch {
        message.error(t('Download-Error'));
        trackEvent(EventNewID.CREATIONS_DOWNLOAD_RESULT, {
          result: StatusEnum.FAIL,
          task_id: businessId,
        });
      }
    },
    [item, batch.modelName],
  );

  const renameFile = useCallback(() => {
    RenameAssetModal.show({
      id: item.id,
      title: item.title,
      onOk(newTitle: string) {
        onRename(item.id, newTitle);
      },
    });
  }, [item.id, item.title, onRename]);

  const deleteFile = useCallback(async () => {
    try {
      if (isLastItem) {
        onDeleteBatch();
        return;
      }
      let res;
      if (item.state === TaskState.SUCCESS) {
        res = await deleteVideo([item.id as number]);
      } else {
        res = await deleteTask([item.id as number]);
      }
      if (res.success) {
        message.success(t('merror.tip.deleteSuccess'));
        onDeleteMedia(item.id);
      } else {
        message.error(t('merror.tip.deleteError'));
      }
    } catch {
      // 与 TaskCard/MediaPreview 一致：吞掉异常
    }
  }, [item, isLastItem, onDeleteBatch, onDeleteMedia]);

  const menuItemBuilders = useMemo(() => {
    const success = state === TaskState.SUCCESS;
    return {
      downloadWithWatermark:
        success && useIdentify === 'freeVip'
          ? {
              key: 'download-with',
              label: t('1fa392999e75851a4964321135e24fa8'),
              onClick: () => {
                downloadFile({
                  download_tier: DownloadTier.FREE,
                  clickType: AssetClickEnum.download_with_watermark,
                });
              },
            }
          : null,
      downloadWithoutWatermark:
        success && useIdentify === 'freeVip'
          ? {
              key: 'download-without',
              label: (
                <span className="flex justify-center">
                  {t('6a804d250c194110364bbba5a76585de')}&nbsp;
                  <ProIcon />
                </span>
              ),
              onClick: () => {
                const { featureName } = getDownloadFeatureInfo([item], batch.modelName);
                trackEvent(EventNewID.ASSET_CLICK, {
                  source: featureName,
                  assetClickType: AssetClickEnum.download_no_watermark,
                });
                PaymentModal.show({
                  feature: featureName,
                  reason: SubscribeReasonEnum.noWatermarkDownload,
                });
              },
            }
          : null,
      download:
        success && useIdentify !== 'freeVip'
          ? {
              key: 'download',
              label: t('merror.operate.download'),
              onClick: () =>
                downloadFile({
                  download_tier: DownloadTier.PAID,
                  clickType: AssetClickEnum.download,
                }),
            }
          : null,
      rename: success
        ? {
            key: 'rename',
            label: t('merror.operate.rename'),
            onClick: () => {
              const { featureName } = getDownloadFeatureInfo([item], batch.modelName);
              trackEvent(EventNewID.ASSET_CLICK, {
                source: featureName,
                assetClickType: AssetClickEnum.rename,
              });
              renameFile();
            },
          }
        : null,
      delete: {
        key: 'delete',
        label: t('Delete'),
        danger: true,
        onClick: () => {
          const { featureName } = getDownloadFeatureInfo([item], batch.modelName);
          trackEvent(EventNewID.ASSET_CLICK, {
            source: featureName,
            assetClickType: AssetClickEnum.delete,
          });
          void deleteFile();
        },
      },
      useToAIVideo: {
        key: 'use-to-ai-video',
        label: t('fc.8a5448b4'),
        onClick: () => {
          const { featureName } = getDownloadFeatureInfo([item], batch.modelName);
          trackEvent(EventNewID.ASSET_CLICK, {
            source: featureName,
            assetClickType: AssetClickEnum.go_to_aivideo,
          });
          const { id, rawData, title } = item;
          const { fileUrl } = rawData || {};
          useImageToVideoStore.getState().setExtraData({
            assetsId: id,
            assetsUrl: fileUrl,
            assetsTitle: title,
          });
          navigate(() => {
            router.replace(`${videoGenerateURL}?tab=${PageTabParam.IMAGE_TO_VIDEO}`);
          });
        },
      },
      useToAvatar2: {
        key: 'use-to-avatar2',
        label: t('fc.17afe20d'),
        onClick: () => {
          const { id, rawData, title } = item;
          const { fileUrl } = rawData || {};
          const { featureName } = getDownloadFeatureInfo([item], batch.modelName);
          trackEvent(EventNewID.ASSET_CLICK, {
            source: featureName,
            assetClickType: AssetClickEnum.go_to_avatarvideo,
          });
          useAvatarProStore.getState().setAssetsInfo({
            assetsId: id,
            assetsUrl: fileUrl,
            assetsTitle: title,
          });
          navigate(() => {
            router.replace(avatarVideoProURL);
          });
        },
      },
    };
  }, [
    state,
    useIdentify,
    downloadFile,
    deleteFile,
    item,
    batch.modelName,
    renameFile,
    router,
    navigate,
  ]);

  const moreMenuItems = useMemo(() => {
    if (state === TaskState.PENDING) {
      return [];
    }
    if (state === TaskState.FAILED) {
      return [menuItemBuilders.delete];
    }
    return [
      menuItemBuilders.downloadWithWatermark,
      menuItemBuilders.downloadWithoutWatermark,
      menuItemBuilders.download,
      F_CLOUD_TASK_TYPE.F_IMAGE_GENERATE === customType && menuItemBuilders.useToAIVideo,
      F_CLOUD_TASK_TYPE.F_IMAGE_GENERATE === customType && menuItemBuilders.useToAvatar2,
      menuItemBuilders.rename,
      menuItemBuilders.delete,
    ].filter(Boolean) as MenuItem[];
  }, [state, menuItemBuilders, customType]);

  const handlePreviewClick = useCallback(() => {
    if (state !== TaskState.SUCCESS) return;
    if (customType === F_CLOUD_TASK_TYPE.F_IMAGE_ENHANCE) {
      ImagePreviewCompareImperative.show({
        src: item.url,
        beforeImgUrl: item.rawData?.extendInfo?.originImgUrl,
        title: item.title,
      });
      return;
    }
    if (type === MediaType.VIDEO) {
      VideoPlayerImperative.show({
        src: item.url,
        title: item.title,
      });
    } else if (type === MediaType.IMAGE) {
      ImagePreviewImperative.show({
        src: item.url,
        title: item.title,
      });
    }
  }, [state, customType, type, item]);

  const imageCover = useMemo(() => {
    const { cover, url } = item;
    if (customType === F_CLOUD_TASK_TYPE.F_IMAGE_ENHANCE) {
      return cover || url;
    }
    if (type === MediaType.PENDING || type === MediaType.VIDEO) {
      return cover;
    }
    return url;
  }, [item, customType, type]);

  const displayTitle = item.title || batch.title;
  const previewClass =
    state === TaskState.SUCCESS
      ? `${styles.preview} ${styles.previewClickable} group`
      : styles.preview;

  return (
    <div className={styles.root}>
      <Tooltip
        title={displayTitle}
        placement="top"
        color="#636ef4"
        classNames={{ body: 'text-[12px]' }}
      >
        <div className={previewClass} onClick={handlePreviewClick}>
          <MemorizeImage cover={imageCover} className="size-full object-contain" />
          <MaskOverlay state={item.state} errorMsg={errorMsg} />
          {state === TaskState.SUCCESS && type === MediaType.VIDEO ? <PlayGlyph /> : null}
          {state !== TaskState.PENDING ? (
            <div className={styles.menuAnchor}>
              <MoreMenu menu={moreMenuItems} icon={MoreIcon} />
            </div>
          ) : null}
        </div>
      </Tooltip>
      <p className={styles.title} title={displayTitle}>
        {displayTitle}
      </p>
    </div>
  );
});

export default ViralMyCreationTile;
